/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Mattress take-back RETURN PLI helpers for the storefront layer.
 *
 * The "Pick Up Old Mattress" service persists on the basket / order as a
 * synthetic `*RETURN` product line item whose sole purpose is to signal a
 * take-back haul-away to D365 (`rtnRequestTriggered.Lines`). It carries a
 * $0 price, no product image, no shopper-facing name, and MUST NOT appear
 * anywhere on the storefront — legacy SFRA filtered it out of every cart /
 * mini-cart / checkout / confirmation template (see
 * `org_ashcomm/scripts/cart/CartUtils.js`, `AshleyPromotionsUtil.js:17`,
 * `MixedModeKit.js:22`, and every ISML that reads `returnMattressSku`).
 *
 * The cartridge-side `sfnext_ashley_hooks/BasketResponseEnricher` filters
 * the PLI out of the SCAPI `basket.get` / `basket.modifyGETResponse`
 * pipeline as the primary line of defense. This module is the client-side
 * safety net that guarantees the storefront NEVER renders the RETURN line,
 * even on surfaces the server hook doesn't cover:
 *
 *   1. `order.get` responses on the order-confirmation page — the SCAPI
 *      Shopper Orders endpoint does not fire the basket enricher hook, so
 *      order responses stream the RETURN PLI through untouched. Without
 *      this client filter a "$0 Product · *RETURN" tile would show up in
 *      the confirmation Order Summary right next to the mattress the
 *      shopper actually paid for.
 *   2. Any surface that iterates `basket.productItems` directly rather
 *      than going through `getMerchandiseProductItems` (cart tile lists,
 *      count badges, availability scans) if a future refactor bypasses
 *      the helper.
 *   3. A defense-in-depth guarantee for the (unlikely) scenario where the
 *      cartridge hook is temporarily disabled, mid-deploy, or short-
 *      circuits due to a caller-supplied response mutation earlier in the
 *      pipeline.
 *
 * Kept in `mattress-takeback/` (separate from `cart/recycling-fee.ts`)
 * because the two concepts are only related by adjacency: the recycling
 * fee is a shopper-facing $10 per-mattress line the shopper explicitly
 * pays for, whereas the RETURN PLI is D365 bookkeeping the shopper
 * never sees.
 */

import type { ShopperBasketsV2, ShopperOrders } from '@/scapi';

/**
 * The RETURN PLI SKU. Mirrors the `returnMattressSku` site preference
 * value deployed on every Ashley B2C instance since the legacy SG
 * migration (`*RETURN`). The pref supports comma-separated tokens for the
 * OHDS glob filter, but the first token is always the literal SKU used
 * when the RETURN PLI is materialized by
 * `sfnext_ashley_hooks/mattressRecycling/TakeBackReturnLineHelper.js`.
 *
 * The `*` prefix is an SFCC convention for logical / bookkeeping product
 * records (records that carry no image, no description, no catalog
 * presence) — no legitimate catalog SKU begins with `*`, so an equality
 * check against this constant is both necessary and sufficient.
 *
 * If ops ever renames the pref, update this constant in lockstep with
 * `RETURN_MATTRESS_SKU` in the cartridge site-import bundle.
 */
export const RETURN_PLI_SKU = '*RETURN';

/** Minimal projection of a product item — mirrors the shape both basket
 *  and order responses expose. Not tied to a single SCAPI schema because
 *  callers hand us items from either endpoint. */
type ReturnPliCandidate = {
    productId?: string | null;
} & Partial<ShopperBasketsV2.schemas['ProductItem'] & ShopperOrders.schemas['ProductItem']>;

/**
 * True when the given basket / order product item is the take-back
 * `*RETURN` PLI.
 *
 * Defensive on undefined / null inputs because callers stream items
 * through map/filter chains where intermediate undefined items are
 * possible. Uses strict equality against `RETURN_PLI_SKU` — no regex or
 * `startsWith` because the deployed pref value is a single stable literal
 * and a broader match would risk false positives against any future
 * legitimate `*`-prefixed catalog SKU.
 */
export function isReturnPliItem(item: ReturnPliCandidate | undefined | null): boolean {
    if (!item) return false;
    return typeof item.productId === 'string' && item.productId === RETURN_PLI_SKU;
}
